/**
 * CoverageHub — the single landing page linking every country coverage
 * view WUA publishes. One card per country with the headline degree count
 * pulled from the shared `/api/methodology/coverage` endpoint, deep-linked
 * to the dedicated country page (`/coverage/uk`, `/coverage/au`) or to the
 * generic advanced-search fallback when a dedicated page doesn't exist yet.
 *
 * The point of this page: from a single URL a family can see WUA covers
 * *N countries · M universities · K degrees*, then pick a country to
 * drill in. Emphasizes the primary-source badge under each — UK=HESA,
 * AU=QILT, IE=HEA, SG=MOE GES, US=Scorecard.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Loader2, ShieldCheck, Globe } from "lucide-react";

// One entry per country card. Order = editorial priority (UK first
// because the depth is deepest, then AU, then the rest by size).
const CARDS = [
  {
    country_name: "United Kingdom",
    country_code: "uk",
    label: "United Kingdom",
    href: "/coverage/uk",
    source: "HESA Graduate Outcomes",
    source_short: "HESA",
    tint: "bg-rose-50 border-rose-200",
    accent: "text-rose-900",
    dot: "bg-rose-500",
    tag: "Deepest coverage — every UK undergrad degree with HMRC-linked salary.",
  },
  {
    country_name: "Australia",
    country_code: "au",
    label: "Australia",
    href: "/coverage/au",
    source: "QILT Graduate Outcomes Survey",
    source_short: "QILT",
    tint: "bg-indigo-50 border-indigo-200",
    accent: "text-indigo-900",
    dot: "bg-indigo-500",
    tag: "Every Group of Eight uni plus every state-registered public uni.",
  },
  {
    country_name: "Singapore",
    country_code: "sg",
    label: "Singapore",
    href: "/advanced?country=SG",
    source: "MOE Graduate Employment Survey",
    source_short: "MOE GES",
    tint: "bg-amber-50 border-amber-200",
    accent: "text-amber-900",
    dot: "bg-amber-500",
    tag: "All 6 autonomous unis + Curtin/JCU/ESSEC/DigiPen branch campuses.",
  },
  {
    country_name: "Ireland",
    country_code: "ie",
    label: "Ireland",
    href: "/countries/ie",
    source: "HEA Graduate Outcomes",
    source_short: "HEA",
    tint: "bg-emerald-50 border-emerald-200",
    accent: "text-emerald-900",
    dot: "bg-emerald-500",
    tag: "The Big-Tech pipeline into Dublin — Google, Meta, LinkedIn, Stripe.",
  },
  {
    country_name: "United States",
    country_code: "us",
    label: "United States",
    href: "/countries/us",
    source: "US Dept of Education College Scorecard",
    source_short: "Scorecard",
    tint: "bg-sky-50 border-sky-200",
    accent: "text-sky-900",
    dot: "bg-sky-500",
    tag: "Federal 5-year post-graduation earnings from actual tax filings.",
  },
  {
    country_name: "Netherlands",
    country_code: "nl",
    label: "Netherlands",
    href: "/countries/nl",
    source: "DUO + Nuffic",
    source_short: "DUO",
    tint: "bg-orange-50 border-orange-200",
    accent: "text-orange-900",
    dot: "bg-orange-500",
    tag: "Every English-taught bachelor across TU Delft, TU/e, RUG, Amsterdam.",
  },
  {
    country_name: "Germany",
    country_code: "de",
    label: "Germany",
    href: "/countries/de",
    source: "DAAD + Hochschulkompass",
    source_short: "DAAD",
    tint: "bg-slate-50 border-slate-200",
    accent: "text-slate-900",
    dot: "bg-slate-500",
    tag: "TUM · LMU · RWTH — near-zero tuition public unis with real destinations.",
  },
  {
    country_name: "France",
    country_code: "fr",
    label: "France",
    href: "/countries/fr",
    source: "Campus France + Grandes Écoles",
    source_short: "Campus France",
    tint: "bg-violet-50 border-violet-200",
    accent: "text-violet-900",
    dot: "bg-violet-500",
    tag: "English-taught Grandes Écoles + French universities with real destinations.",
  },
];


function CountryCard({ card, counts }) {
  const c = counts || {};
  return (
    <Link
      to={card.href}
      className={`group flex flex-col rounded-lg border p-5 sm:p-6 hover:shadow-md transition-all min-w-0 ${card.tint}`}
      data-testid={`coverage-hub-card-${card.country_code}`}
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2.5">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${card.dot}`} />
          <h2 className={`text-lg font-editorial tracking-tight ${card.accent}`}>
            {card.label}
          </h2>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <div className="text-2xl font-serif tabular-nums text-slate-900 leading-none">
            {(c.universities_have ?? "—").toLocaleString?.() ?? "—"}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">
            universities
          </div>
        </div>
        <div>
          <div className="text-2xl font-serif tabular-nums text-slate-900 leading-none">
            {(c.degrees_have ?? "—").toLocaleString?.() ?? "—"}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">
            UG degrees
          </div>
        </div>
      </div>

      <p className="text-[12px] text-slate-600 flex-1 mb-3">
        {card.tag}
      </p>

      <div className="flex items-center gap-1.5 text-[10px] font-underground-sc tracking-wider text-emerald-800 border-t border-slate-200 pt-3 -mx-1 px-1">
        <ShieldCheck className="h-3 w-3" />
        <span className="truncate">Primary source: {card.source_short}</span>
      </div>
    </Link>
  );
}


export default function CoverageHub() {
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let live = true;
    api
      .get("/methodology/coverage")
      .then((r) => live && setState({ loading: false, data: r.data, err: null }))
      .catch((e) => live && setState({ loading: false, data: null, err: e?.message || "failed" }));
    return () => {
      live = false;
    };
  }, []);

  if (state.loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-24 flex items-center justify-center text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading coverage…
      </div>
    );
  }
  if (state.err || !state.data) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-24 text-slate-600">
        Couldn't load coverage: {state.err || "empty response"}
      </div>
    );
  }
  const rows = state.data.rows || [];
  const byCountry = Object.fromEntries(rows.map((r) => [r.country, r]));

  const totalUnis = rows.reduce((n, r) => n + (r.universities_have || 0), 0);
  const totalDegrees = rows.reduce((n, r) => n + (r.degrees_have || 0), 0);
  const totalCountries = rows.filter((r) => (r.universities_have || 0) > 0).length;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14" data-testid="coverage-hub-page">
      <header className="mb-8 sm:mb-10">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
          <Globe className="h-3 w-3" />
          Coverage
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-editorial tracking-tight text-slate-900">
          Every university WUA covers, in one place.
        </h1>
        <p className="mt-4 max-w-3xl text-base text-slate-600">
          WhichUniversityAdvisor operates on primary-source data —
          government statistics, university-published prospectuses,
          alumni-verified destinations. This page shows the full footprint
          of our current coverage across every country, with a direct link
          to each country's index.
        </p>

        <div className="mt-6 grid grid-cols-3 gap-6 sm:gap-10">
          <div data-testid="coverage-hub-total-countries" className="flex flex-col">
            <span className="text-3xl font-serif tracking-tight text-slate-900">
              {totalCountries}
            </span>
            <span className="mt-0.5 text-[11px] uppercase tracking-wider text-slate-500">
              countries
            </span>
          </div>
          <div data-testid="coverage-hub-total-unis" className="flex flex-col">
            <span className="text-3xl font-serif tracking-tight text-slate-900">
              {totalUnis.toLocaleString()}
            </span>
            <span className="mt-0.5 text-[11px] uppercase tracking-wider text-slate-500">
              universities indexed
            </span>
          </div>
          <div data-testid="coverage-hub-total-degrees" className="flex flex-col">
            <span className="text-3xl font-serif tracking-tight text-slate-900">
              {totalDegrees.toLocaleString()}
            </span>
            <span className="mt-0.5 text-[11px] uppercase tracking-wider text-slate-500">
              undergrad degrees
            </span>
          </div>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((card) => (
          <CountryCard
            key={card.country_code}
            card={card}
            counts={byCountry[card.country_name]}
          />
        ))}
      </div>

      <footer className="mt-10 text-[12px] text-slate-500">
        Counts refresh nightly from the master coverage benchmarks table.
        Countries without a dedicated coverage page (IE, US, NL, DE, FR)
        deep-link into the country landing view. See {" "}
        <Link to="/methodology/data-sources" className="underline hover:no-underline">
          /methodology/data-sources
        </Link>{" "}
        for every primary source we ingest.
      </footer>
    </div>
  );
}
